import { Button, Checkbox, Dropdown, Tooltip } from 'antd';
import { ControlOutlined } from '@ant-design/icons';
import { ReactNode } from 'react';
import cn from 'classnames';
import { ColumnsType, ColumnType } from 'antd/lib/table';
import { columnKey, ColumnKey } from '../../utils/table-columns.utils';
import css from './ColumnSelector.module.less';

interface ColumnSelectorProps<T> {
	/** The unstyled columns (with their original string titles) to offer as toggles. */
	columns: ColumnsType<T>;
	isHidden: (key: ColumnKey) => boolean;
	alwaysVisible: Set<string>;
	onSetHidden: (key: ColumnKey, hidden: boolean) => void;
	className?: string;
}

/** A human label for the toggle: the column's string title, else its key. */
function columnLabel<T>(col: ColumnType<T>, key: string): ReactNode {
	return typeof col.title === 'string' ? col.title : key;
}

/**
 * A "Columns" dropdown that toggles which columns a table shows. Uses a plain
 * overlay (not an antd Menu) so the panel stays open while the user ticks
 * several columns in a row.
 */
export function ColumnSelector<T>({ columns, isHidden, alwaysVisible, onSetHidden, className }: ColumnSelectorProps<T>) {
	const items = columns
		.map(col => {
			const key = columnKey(col as ColumnType<T>);
			return key == null ? null : { key, label: columnLabel(col as ColumnType<T>, key) };
		})
		.filter((item): item is { key: string; label: ReactNode } => item != null);

	const overlay = (
		<div className={css.panel}>
			{items.map(({ key, label }) => {
				const locked = alwaysVisible.has(key);
				return (
					<label key={key} className={css.row}>
						<Checkbox
							checked={locked || !isHidden(key)}
							disabled={locked}
							onChange={e => onSetHidden(key, !e.target.checked)}
						/>
						<span className={css.label}>{label}</span>
					</label>
				);
			})}
		</div>
	);

	return (
		<Dropdown
			overlay={overlay}
			trigger={['click']}
			placement="bottomRight"
			getPopupContainer={() => document.body}
		>
			<span className={cn(css.trigger, className)}>
				<Tooltip title="Select Columns">
					<Button type="text" size="small" icon={<ControlOutlined />} aria-label="Select columns" />
				</Tooltip>
			</span>
		</Dropdown>
	);
}
